import { createReducer, on } from '@ngrx/store';
import { IDamageEntryState, IIncidentFormDataState } from './damages.state';
import {
  addDamageToIncident,
  addNewDamageToDamagesTable,
  createIncident,
  createIncidentFailure,
  createIncidentSuccess,
  getAvailableSurveyPointsForVIN,
  getDamageHistoriesFailure,
  getDamageHistoriesSuccess,
  getSurveyPointsForVINFailure,
  getSurveyPointsForVINSuccess,
  loadDamageHistories,
  removeNewDamageFromIncident,
  resetDamageEntryState,
  setBatchUploadData,
  setDamage,
  setDamageEntryPopupOpenFlag,
  setSurveyPoint,
  startNewDamageEntryProcess,
  startNewDamageEntryProcessCompleted,
  startNewDamageEntryProcessFailed,
} from './damages.actions';
import {
  IDamage,
  IDamageRequest,
  IExistingDamages,
  IIncident,
  IIncidentRequest,
  IVehicle,
} from '../damages.models';
import { SelectListOption } from '@bmw-ds/components';
import { ISurveyPoint } from '../../admin/locations/locations.models';
import { DateTime } from 'luxon';

export const damageEntryInitialState: IDamageEntryState = {
  vehicle: {} as IVehicle,
  incident: null,
  loading: false,
  dataLoading: false,
  savingLoading: false,
  damageEntryPopupOpenFlag: false,
};

export const damageEntryReducer = createReducer(
  damageEntryInitialState,
  on(
    startNewDamageEntryProcess,
    (state, { vehicle, surveyPoint, incident, damage }) => {
      return {
        ...state,
        vehicle: vehicle as IVehicle,
        incidentFormData: {
          ...state.incidentFormData,
          selectedSurveyPoint: surveyPoint as ISurveyPoint,
          selectedIncident: incident as IIncident,
          selectedDamage: (damage as IDamage) ?? undefined,
        } as IIncidentFormDataState,
        loading: true,
        dataLoading: false,
        savingLoading: false,
        incident: null,
      };
    }
  ),
  on(
    startNewDamageEntryProcessCompleted,
    (
      state,
      {
        vehicleFromApi,
        vehicleOptions,
        surveyPoint,
        transportStages,
        existingDamages,
        returnDamages,
      }
    ) => {
      return {
        ...state,
        vehicle: {
          ...(vehicleFromApi as IVehicle),
          vehicleOptions: vehicleOptions,
        },
        incident: {
          ...state.incident,
          surveyPointId: surveyPoint.id,
          existingDamages,
          returnDamages,
        } as IIncidentRequest,
        incidentFormData: {
          ...state.incidentFormData,
          transportStageOptions:
            transportStages?.map((item) => ({
              id: `${item.id}:${item.fromSurveyPointId}:${item.toSurveyPointId}`,
              label: `${item.fromSurveyPoint} - ${item.toSurveyPoint}`,
            })) ?? [],
          transportStages: transportStages,
        } as unknown as IIncidentFormDataState,
        loading: false,
        dataLoading: false,
        savingLoading: false,
      };
    }
  ),
  on(startNewDamageEntryProcessFailed, (state) => {
    return {
      ...state,
      loading: false,
      dataLoading: false,
      savingLoading: false,
    };
  }),
  on(removeNewDamageFromIncident, (state, { damage }) => {
    const newDamages = [...(state.incident?.newDamages ?? [])];
    const newDamagesConverted = [
      ...(state.incident?.newDamagesConverted ?? []),
    ];
    const existingDamages = [...(state.incident?.existingDamages ?? [])];
    const newDamageIndex = newDamages.findIndex(
      (item) => item.temporaryId === damage?.temporaryId
    );
    const newDamageConvertedIndex = newDamagesConverted.findIndex(
      (item) => item.temporaryId === damage?.temporaryId
    );
    const existingIncidentIndex = existingDamages.findIndex((item) =>
      item.damages.some(
        (incidentDamage) => incidentDamage.temporaryId === damage?.temporaryId
      )
    );

    if (newDamageIndex > -1) {
      newDamages.splice(newDamageIndex, 1);
    }
    if (newDamageConvertedIndex > -1) {
      newDamagesConverted.splice(newDamageConvertedIndex, 1);
    }
    if (existingIncidentIndex > -1) {
      existingDamages.splice(existingIncidentIndex, 1);
    }

    return {
      ...state,
      incident: {
        ...state.incident,
        newDamages,
        newDamagesConverted,
        existingDamages,
      } as unknown as IIncidentRequest,
    };
  }),
  on(getAvailableSurveyPointsForVIN, (state) => {
    return {
      ...state,
      dataLoading: true,
    };
  }),
  on(getSurveyPointsForVINSuccess, (state, { availableSurveyPoints }) => {
    return {
      ...state,
      dataLoading: false,
      incidentFormData: {
        ...state.incidentFormData,
        surveyPoints: availableSurveyPoints,
        surveyPointOptions: availableSurveyPoints.map(
          (surveyPoint) =>
            ({
              id: surveyPoint.name + '$' + surveyPoint.locationEntryDate!,
              label:
                surveyPoint.name +
                ' ' +
                convertDateToRouteStage(
                  surveyPoint as ISurveyPoint,
                  availableSurveyPoints as ISurveyPoint[]
                ),
            }) as unknown as SelectListOption
        ),
      } as IIncidentFormDataState,
    };
  }),
  on(getSurveyPointsForVINFailure, (state) => {
    return {
      ...state,
      dataLoading: false,
    };
  }),
  on(addDamageToIncident, (state, { damage }) => {
    const currentDamages = state.incident?.newDamages ?? [];
    const existingDamageIndex = currentDamages.findIndex(
      (item) => item.temporaryId === damage.temporaryId
    );

    let newDamages: Partial<IDamageRequest>[];
    if (existingDamageIndex > -1) {
      newDamages = currentDamages.map((item, index) =>
        index === existingDamageIndex ? damage : item
      );
    } else {
      newDamages = [...currentDamages, damage];
    }

    return {
      ...state,
      incident: {
        ...state.incident,
        newDamages,
      } as unknown as IIncidentRequest,
    };
  }),
  on(createIncident, (state) => {
    return {
      ...state,
      savingLoading: true,
    };
  }),
  on(createIncidentSuccess, (state) => {
    return {
      ...state,
      savingLoading: false,
    };
  }),
  on(createIncidentFailure, (state) => {
    return {
      ...state,
      savingLoading: false,
    };
  }),
  on(setSurveyPoint, (state, { surveyPoint }) => {
    return {
      ...state,
      incidentFormData: {
        ...state.incidentFormData,
        selectedSurveyPoint: surveyPoint,
      } as IIncidentFormDataState,
    };
  }),
  on(setDamage, (state, { damage }) => {
    return {
      ...state,
      incidentFormData: {
        ...state.incidentFormData,
        selectedDamage: damage,
      } as IIncidentFormDataState,
    };
  }),
  on(addNewDamageToDamagesTable, (state, { damage, damageConverted }) => {
    const damageCopy = { ...damage };

    if (damageCopy.files) {
      damageCopy.files = [];
    }

    const newIncidentWithDamage = {
      damages: [damageCopy],
    } as unknown as IIncident;

    const currentExistingDamages = state.incident?.existingDamages ?? [];
    let existingDamages: IIncident[];

    const existingIncidentIndex = currentExistingDamages.findIndex((item) =>
      item.damages.some(
        (incidentDamage) =>
          incidentDamage.temporaryId === damageCopy.temporaryId
      )
    );

    if (existingIncidentIndex > -1) {
      existingDamages = currentExistingDamages.map((incident, index) => {
        if (index !== existingIncidentIndex) return incident;
        return {
          ...incident,
          damages: [
            ...incident.damages.filter(
              (d) => d.temporaryId !== damageCopy.temporaryId
            ),
            damageCopy as unknown as IDamage,
          ],
        };
      });
    } else {
      existingDamages = [...currentExistingDamages, newIncidentWithDamage];
    }

    const currentNewDamagesConverted =
      state.incident?.newDamagesConverted ?? [];
    let newDamagesConverted: IExistingDamages[];

    const newExistingDamageIndex = currentNewDamagesConverted.findIndex(
      (item) => item.temporaryId === damageConverted.temporaryId
    );

    if (newExistingDamageIndex > -1) {
      newDamagesConverted = currentNewDamagesConverted.map((item, index) =>
        index === newExistingDamageIndex ? damageConverted : item
      );
    } else {
      newDamagesConverted = [...currentNewDamagesConverted, damageConverted];
    }

    return {
      ...state,
      incident: {
        ...state.incident,
        vehicleConditions: state.incident!.vehicleConditions ?? [],
        causerId: state.incident!.causerId ?? '',
        creatorId: state.incident!.creatorId ?? '',
        fromSurveyPointId: state.incident!.fromSurveyPointId ?? '',
        toSurveyPointId: state.incident!.toSurveyPointId ?? '',
        incidentRegistrationSurveyPointId:
          state.incident!.incidentRegistrationSurveyPointId ?? '',
        isOutsideOfBusinessHours:
          state.incident!.isOutsideOfBusinessHours ?? false,
        locationEntryDate: state.incident!.locationEntryDate ?? '',
        vehicleVin: state.incident!.vehicleVin,
        existingDamages,
        newDamages: state.incident?.newDamages ?? [],
        newDamagesConverted,
      },
    };
  }),
  on(resetDamageEntryState, () => {
    return damageEntryInitialState;
  }),
  on(setDamageEntryPopupOpenFlag, (state, { damageEntryPopupOpenFlag }) => {
    return {
      ...state,
      damageEntryPopupOpenFlag: damageEntryPopupOpenFlag,
    };
  }),
  on(setBatchUploadData, (state, { batchUpload }) => {
    return {
      ...state,
      batchUploadData: batchUpload,
    };
  }),
  on(loadDamageHistories, (state) => {
    return {
      ...state,
      dataLoading: true,
    };
  }),
  on(getDamageHistoriesSuccess, (state, { damageHistories }) => {
    return {
      ...state,
      damageHistories,
      dataLoading: false,
    };
  }),
  on(getDamageHistoriesFailure, (state, { error }) => {
    return {
      ...state,
      error,
      damageHistories: [],
      dataLoading: false,
    };
  })
);

const convertDateToRouteStage = (
  surveyPoint: ISurveyPoint,
  allOptions: ISurveyPoint[]
): string => {
  if (
    surveyPoint.locationEntryDate &&
    allOptions.filter((option) => option.id === surveyPoint.id).length > 1
  ) {
    const dateTime = DateTime.fromMillis(
      Number(surveyPoint.locationEntryDate),
      {
        zone: 'utc',
      }
    ).setZone('Europe/Berlin');
    if (dateTime.isValid) {
      return '(' + dateTime.toFormat('dd.MM.yyyy HH:mm') + ')';
    }
  }
  return '';
};
